import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import {
  ArrowLeft,
  ArrowRight,
  RefreshCw,
  Sparkles,
  Trophy,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  GraduationCap,
} from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import { colors } from "../config/site";

/**
 * Rent Readiness Self-Assessment
 *
 * 10 questions across 4 categories. Each answer carries a point value.
 * Final score 0-100 is bucketed into:
 *   80-100 → "Strong" (green)
 *   55-79  → "Building" (gold)
 *   0-54   → "Get Started" (terracotta)
 *
 * Personalized tips are surfaced based on which categories scored lowest.
 */

const QUESTIONS = [
  {
    id: "income",
    category: "Income",
    question: "Which best describes your income situation right now?",
    options: [
      { label: "Steady job for 6+ months", points: 12 },
      { label: "Working — but less than 6 months", points: 8 },
      { label: "Part-time, gig, or seasonal work", points: 5 },
      { label: "Currently between jobs", points: 2 },
    ],
  },
  {
    id: "rent_ratio",
    category: "Income",
    question: "How much of your monthly take-home pay would rent eat up?",
    options: [
      { label: "Less than 30%", points: 12 },
      { label: "30–40%", points: 9 },
      { label: "40–50%", points: 5 },
      { label: "More than 50% (or not sure)", points: 2 },
    ],
  },
  {
    id: "savings",
    category: "Savings",
    question: "Do you have money saved for a security deposit and first month's rent?",
    options: [
      { label: "Yes — full amount saved", points: 12 },
      { label: "About half saved", points: 8 },
      { label: "A little — just started", points: 4 },
      { label: "Not yet", points: 1 },
    ],
  },
  {
    id: "emergency_fund",
    category: "Savings",
    question: "If your car broke down tomorrow ($500 fix), could you cover it without missing rent?",
    options: [
      { label: "Yes, easily", points: 10 },
      { label: "Yes, but it would be tight", points: 7 },
      { label: "I'd have to put it on a card", points: 3 },
      { label: "No — that would set me back", points: 1 },
    ],
  },
  {
    id: "credit",
    category: "Credit",
    question: "Do you know your current credit score?",
    options: [
      { label: "Yes — and it's 650+", points: 12 },
      { label: "Yes — it's under 650", points: 7 },
      { label: "Not sure, but I check sometimes", points: 4 },
      { label: "I've never checked", points: 2 },
    ],
  },
  {
    id: "rental_history",
    category: "Rental History",
    question: "What does your rental history look like?",
    options: [
      { label: "Strong — never missed a payment, good references", points: 12 },
      { label: "Mostly good — maybe one late payment", points: 8 },
      { label: "Some bumps — a missed month or two", points: 4 },
      { label: "Prior eviction or gap in rental history", points: 2 },
    ],
  },
  {
    id: "documents",
    category: "Rental History",
    question: "Do you have these documents ready: photo ID, pay stubs, proof of income, references?",
    options: [
      { label: "All of them, organized", points: 10 },
      { label: "Most of them", points: 7 },
      { label: "Some — I'd need to gather others", points: 4 },
      { label: "I haven't started gathering them", points: 1 },
    ],
  },
  {
    id: "lease_knowledge",
    category: "Knowledge",
    question: "How confident are you reading and understanding a lease?",
    options: [
      { label: "Very confident — I know what to look for", points: 10 },
      { label: "Mostly comfortable", points: 7 },
      { label: "I sign it, but I don't always understand every part", points: 4 },
      { label: "I've felt overwhelmed by leases", points: 2 },
    ],
  },
  {
    id: "tenant_rights",
    category: "Knowledge",
    question: "Do you know your basic tenant rights in Pennsylvania?",
    options: [
      { label: "Yes — fair amount", points: 8 },
      { label: "A little", points: 5 },
      { label: "Not really", points: 2 },
      { label: "I've never thought about it", points: 1 },
    ],
  },
  {
    id: "support",
    category: "Knowledge",
    question: "If you fell behind on rent, would you know where to get help?",
    options: [
      { label: "Yes — I know my options", points: 8 },
      { label: "I'd figure it out", points: 5 },
      { label: "Probably not in time", points: 2 },
      { label: "No idea where to start", points: 1 },
    ],
  },
];

const CATEGORY_TIPS = {
  Income: {
    title: "Income",
    tip: "Lenders and landlords look for steady, predictable income. If you're new to a job or working multiple gigs, gather 3 months of pay records and consider asking your employer for a written income-verification letter.",
  },
  Savings: {
    title: "Savings",
    tip: "Aim to have first month's rent + security deposit + a $500 cushion saved before you sign. Pay yourself first — set up an automatic transfer of even $20 per paycheck into a separate account.",
  },
  Credit: {
    title: "Credit",
    tip: "Most landlords check credit. Pull your credit report at annualcreditreport.com (the only official source for the free annual report). Pay down small balances first — they have the fastest impact on your score.",
  },
  "Rental History": {
    title: "Rental History",
    tip: "Keep every receipt and reference letter. If you've had past issues, prepare a short, honest letter explaining what happened and what's changed — most landlords appreciate transparency.",
  },
  Knowledge: {
    title: "Knowledge",
    tip: "Knowing your lease and your rights protects you long after move-in day. Our Lease Literacy workshop and the Tenant-Friendly Maintenance Guide are great places to start.",
  },
};

const MAX_SCORE = QUESTIONS.reduce(
  (sum, q) => sum + Math.max(...q.options.map((o) => o.points)),
  0
);

const scoreToBucket = (pct) => {
  if (pct >= 80)
    return {
      label: "Strong",
      color: colors.primary,
      icon: Trophy,
      headline: "You're in great shape.",
      summary:
        "You've got the foundation in place for stable, long-term renting. Keep the habits going — and start thinking about your next step: maybe homeownership.",
    };
  if (pct >= 55)
    return {
      label: "Building",
      color: colors.gold,
      icon: TrendingUp,
      headline: "You're well on your way.",
      summary:
        "You're closer than you think. A few focused improvements in the areas below will put you in a strong position to rent confidently — and stay housed.",
    };
  return {
    label: "Get Started",
    color: colors.terracotta,
    icon: AlertCircle,
    headline: "Let's build your foundation.",
    summary:
      "Everyone starts somewhere — and the fact that you're here is a great first step. Focus on the priority areas below, and join one of our workshops to keep momentum going.",
  };
};

const RentReadinessQuiz = () => {
  const [step, setStep] = useState(0); // 0 = intro, 1..N = questions, N+1 = result
  const [answers, setAnswers] = useState({});

  const totalQuestions = QUESTIONS.length;
  const isIntro = step === 0;
  const isResult = step === totalQuestions + 1;
  const currentQ = !isIntro && !isResult ? QUESTIONS[step - 1] : null;

  const handleAnswer = (q, optIdx) => {
    setAnswers((prev) => ({ ...prev, [q.id]: optIdx }));
  };

  const next = () => setStep((s) => s + 1);
  const back = () => setStep((s) => Math.max(0, s - 1));
  const restart = () => {
    setAnswers({});
    setStep(0);
  };

  // Result calculation
  const totalPoints = QUESTIONS.reduce((sum, q) => {
    const optIdx = answers[q.id];
    if (typeof optIdx === "number") return sum + q.options[optIdx].points;
    return sum;
  }, 0);
  const pct = Math.round((totalPoints / MAX_SCORE) * 100);
  const bucket = scoreToBucket(pct);
  const BucketIcon = bucket.icon;

  // Category scores → identify lowest-scoring categories for tip surfacing
  const categoryScores = {};
  QUESTIONS.forEach((q) => {
    const optIdx = answers[q.id];
    const pts = typeof optIdx === "number" ? q.options[optIdx].points : 0;
    const max = Math.max(...q.options.map((o) => o.points));
    if (!categoryScores[q.category])
      categoryScores[q.category] = { points: 0, max: 0 };
    categoryScores[q.category].points += pts;
    categoryScores[q.category].max += max;
  });
  const categoryPcts = Object.entries(categoryScores).map(([cat, s]) => ({
    cat,
    pct: Math.round((s.points / s.max) * 100),
  }));
  const lowestCategories = [...categoryPcts]
    .sort((a, b) => a.pct - b.pct)
    .slice(0, 3)
    .filter((c) => c.pct < 80);

  // Progress bar
  const progressPct = isIntro
    ? 0
    : isResult
    ? 100
    : Math.round(((step - 1) / totalQuestions) * 100);

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.cream }}>
      <Header />
      <main className="pt-28 pb-20 px-4">
        <div className="container mx-auto max-w-3xl">
          {/* Back to home */}
          <Link
            to="/"
            className="inline-flex items-center text-sm font-semibold mb-6"
            style={{ color: colors.primary }}
            data-testid="quiz-back-home"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>

          {/* Card */}
          <div
            className="rounded-3xl shadow-lg overflow-hidden"
            style={{ backgroundColor: colors.white }}
          >
            {/* Progress bar */}
            <div className="h-1.5 w-full" style={{ backgroundColor: colors.cream }}>
              <div
                className="h-full transition-all duration-500"
                style={{
                  width: `${progressPct}%`,
                  background: `linear-gradient(90deg, ${colors.primary}, ${colors.terracotta})`,
                }}
                data-testid="quiz-progress-bar"
              />
            </div>

            <div className="p-8 lg:p-12">
              {/* INTRO */}
              {isIntro && (
                <div data-testid="quiz-intro">
                  <div
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-5"
                    style={{ backgroundColor: colors.terracotta + "22", color: colors.terracottaDark }}
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    Self-Assessment
                  </div>
                  <h1
                    className="text-3xl lg:text-5xl font-bold leading-tight mb-5"
                    style={{ color: colors.charcoal }}
                  >
                    Rent Readiness Quiz
                  </h1>
                  <p className="text-lg mb-6" style={{ color: colors.textMuted }}>
                    Ten quick questions. Get a personalized rent-readiness score
                    plus tips on what to focus on next. <strong>No email required.</strong>
                  </p>
                  <div
                    className="rounded-2xl p-5 mb-7 flex gap-4 items-start"
                    style={{ backgroundColor: colors.cream }}
                  >
                    <GraduationCap
                      className="w-6 h-6 flex-shrink-0 mt-0.5"
                      style={{ color: colors.primary }}
                    />
                    <div>
                      <p
                        className="text-xs uppercase tracking-widest font-bold mb-1"
                        style={{ color: colors.sage }}
                      >
                        How it works
                      </p>
                      <p className="text-sm" style={{ color: colors.charcoal }}>
                        Pick the answer closest to your situation. Honest answers give
                        you the most useful results. Takes about 3 minutes.
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={next}
                    className="rounded-full px-7 py-6 text-base font-semibold text-white shadow-md hover:shadow-xl transition-all"
                    style={{ backgroundColor: colors.primary }}
                    data-testid="quiz-start-btn"
                  >
                    Start the Quiz
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              )}

              {/* QUESTION */}
              {currentQ && (
                <div data-testid={`quiz-question-${step}`}>
                  <p
                    className="text-xs uppercase tracking-widest font-bold mb-2"
                    style={{ color: colors.terracotta }}
                  >
                    Question {step} of {totalQuestions} · {currentQ.category}
                  </p>
                  <h2
                    className="text-2xl lg:text-3xl font-bold mb-7"
                    style={{ color: colors.charcoal }}
                  >
                    {currentQ.question}
                  </h2>
                  <div className="space-y-3 mb-8">
                    {currentQ.options.map((opt, idx) => {
                      const selected = answers[currentQ.id] === idx;
                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => handleAnswer(currentQ, idx)}
                          className="w-full text-left rounded-2xl p-4 lg:p-5 transition-all border-2"
                          style={{
                            backgroundColor: selected ? colors.primary : colors.white,
                            color: selected ? colors.white : colors.charcoal,
                            borderColor: selected ? colors.primary : "#E6DFD2",
                          }}
                          data-testid={`quiz-option-${currentQ.id}-${idx}`}
                        >
                          <span className="font-semibold">{opt.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <Button
                      onClick={back}
                      variant="ghost"
                      disabled={step === 1}
                      className="rounded-full px-5"
                      data-testid="quiz-back-btn"
                    >
                      <ArrowLeft className="mr-2 w-4 h-4" />
                      Back
                    </Button>
                    <Button
                      onClick={next}
                      disabled={typeof answers[currentQ.id] !== "number"}
                      className="rounded-full px-6 py-5 font-semibold text-white"
                      style={{ backgroundColor: colors.terracotta }}
                      data-testid="quiz-next-btn"
                    >
                      {step === totalQuestions ? "See My Result" : "Next"}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* RESULT */}
              {isResult && (
                <div data-testid="quiz-result">
                  <div
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-5"
                    style={{ backgroundColor: bucket.color + "22", color: bucket.color }}
                  >
                    <BucketIcon className="w-3.5 h-3.5" />
                    Your Result: {bucket.label}
                  </div>
                  <h2
                    className="text-3xl lg:text-4xl font-bold mb-3"
                    style={{ color: colors.charcoal }}
                  >
                    {bucket.headline}
                  </h2>
                  {/* Big score */}
                  <div className="flex items-center gap-5 mb-6">
                    <div
                      className="relative flex items-center justify-center w-28 h-28 rounded-full"
                      style={{
                        background: `conic-gradient(${bucket.color} ${pct}%, #E6DFD2 0)`,
                      }}
                    >
                      <div
                        className="w-20 h-20 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: colors.white }}
                      >
                        <span
                          className="text-3xl font-bold"
                          style={{ color: bucket.color }}
                          data-testid="quiz-result-score"
                        >
                          {pct}
                        </span>
                      </div>
                    </div>
                    <p className="text-base" style={{ color: colors.textMuted }}>
                      {bucket.summary}
                    </p>
                  </div>

                  {/* Category breakdown */}
                  <div className="rounded-2xl p-5 mb-7" style={{ backgroundColor: colors.cream }}>
                    <p
                      className="text-xs uppercase tracking-widest font-bold mb-4"
                      style={{ color: colors.sage }}
                    >
                      Your category breakdown
                    </p>
                    <div className="space-y-3">
                      {categoryPcts.map((c) => (
                        <div key={c.cat}>
                          <div
                            className="flex justify-between text-sm font-semibold mb-1"
                            style={{ color: colors.charcoal }}
                          >
                            <span>{c.cat}</span>
                            <span>{c.pct}%</span>
                          </div>
                          <div
                            className="h-2 rounded-full overflow-hidden"
                            style={{ backgroundColor: "#E6DFD2" }}
                          >
                            <div
                              className="h-full transition-all"
                              style={{
                                width: `${c.pct}%`,
                                backgroundColor:
                                  c.pct >= 80
                                    ? colors.primary
                                    : c.pct >= 55
                                    ? colors.gold
                                    : colors.terracotta,
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Priority tips */}
                  {lowestCategories.length > 0 && (
                    <div className="mb-7">
                      <h3 className="text-xl font-bold mb-4" style={{ color: colors.charcoal }}>
                        Where to focus next
                      </h3>
                      <div className="space-y-3">
                        {lowestCategories.map((c) => {
                          const tip = CATEGORY_TIPS[c.cat];
                          if (!tip) return null;
                          return (
                            <div
                              key={c.cat}
                              className="rounded-2xl p-5 flex gap-4 items-start"
                              style={{
                                backgroundColor: colors.ivory,
                                borderLeft: `4px solid ${colors.terracotta}`,
                              }}
                            >
                              <CheckCircle2
                                className="w-5 h-5 flex-shrink-0 mt-0.5"
                                style={{ color: colors.terracotta }}
                              />
                              <div>
                                <p
                                  className="font-bold mb-1"
                                  style={{ color: colors.charcoal }}
                                >
                                  {tip.title}
                                </p>
                                <p className="text-sm" style={{ color: colors.textMuted }}>
                                  {tip.tip}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div
                    className="rounded-3xl p-6 lg:p-7 text-white"
                    style={{
                      background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
                    }}
                  >
                    <h3 className="text-xl lg:text-2xl font-bold mb-2">
                      Want help with your weakest area?
                    </h3>
                    <p className="text-sm opacity-90 mb-5">
                      Our workshops walk you through each category step-by-step.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Link to="/resources" data-testid="quiz-cta-resources">
                        <Button
                          className="rounded-full px-6 py-5 font-semibold w-full sm:w-auto"
                          style={{ backgroundColor: colors.terracotta, color: colors.white }}
                        >
                          Browse Resources
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      </Link>
                      <Link to="/contact" data-testid="quiz-cta-contact">
                        <Button
                          className="rounded-full px-6 py-5 font-semibold w-full sm:w-auto border-white/40 border bg-transparent hover:bg-white/10"
                        >
                          Talk to Our Team
                        </Button>
                      </Link>
                    </div>
                  </div>

                  <div className="flex justify-center mt-7">
                    <Button
                      onClick={restart}
                      variant="ghost"
                      className="rounded-full"
                      data-testid="quiz-restart-btn"
                    >
                      <RefreshCw className="mr-2 w-4 h-4" />
                      Retake Quiz
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default RentReadinessQuiz;
